import { useState, useEffect, useMemo } from "react";

export interface StageExtraFieldDef {
  id: string | number;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  sortOrder?: number;
}

export interface StageDef {
  id?: string | number;
  name: string;
  color?: string;
  required?: boolean;
  extraFields?: StageExtraFieldDef[];
}

type Props = {
  leadName: string;
  targetStage: string;
  stageDefinitions?: StageDef[];
  onClose: () => void;
  onConfirm: (extraFieldValues: Record<string, string>) => void;
};

export function MoveToStageModal({ leadName, targetStage, stageDefinitions = [], onClose, onConfirm }: Props) {
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const targetStageObj = useMemo(() => {
    return stageDefinitions.find((s) => s.name?.toLowerCase().trim() === targetStage?.toLowerCase().trim());
  }, [stageDefinitions, targetStage]);

  const extraFields: StageExtraFieldDef[] = useMemo(() => {
    return Array.isArray(targetStageObj?.extraFields) ? targetStageObj.extraFields : [];
  }, [targetStageObj]);

  useEffect(() => {
    setExtraFieldValues({});
    setErrors({});
  }, [targetStage]);

  const updateFieldValue = (fieldKey: string, value: string) => {
    setExtraFieldValues((prev) => ({ ...prev, [fieldKey]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const normalizeType = (t?: string): "text" | "number" | "currency" | "date" | "time" | "dropdown" | "textarea" | "toggle" => {
    if (!t) return "text";
    const low = t.toLowerCase().trim();
    if (low.includes("num")) return "number";
    if (low.includes("curren") || low.includes("price") || low.includes("amount") || low.includes("deal")) return "currency";
    if (low.includes("date")) return "date";
    if (low.includes("time")) return "time";
    if (low.includes("drop") || low.includes("select")) return "dropdown";
    if (low.includes("area")) return "textarea";
    if (low.includes("toggle") || low.includes("bool") || low.includes("check")) return "toggle";
    return "text";
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    extraFields.forEach((f) => {
      const key = f.label || String(f.id);
      if (f.required) {
        const val = extraFieldValues[key];
        if (!val || !val.trim()) {
          newErrors[key] = `${f.label || "This field"} is required`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onConfirm(extraFieldValues);
    onClose();
  };

  const stageColor = targetStageObj?.color || "#6366f1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Moving to {targetStage}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {leadName} →{" "}
              <span className="inline-flex items-center gap-1.5 font-medium">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColor }} />
                {targetStage}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-5">
          {extraFields.length > 0 ? (
            <>
              <p className="text-xs text-gray-500 font-medium mb-4">
                Please fill the required information configured for this stage:
              </p>
              <div className="space-y-3.5">
                {extraFields.map((field) => {
                  const fieldKey = field.label || String(field.id);
                  const fType = normalizeType(field.type);
                  const hasError = !!errors[fieldKey];

                  return (
                    <div key={fieldKey}>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>

                      {fType === "number" && (
                        <input
                          type="number"
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          placeholder={`Enter ${field.label.toLowerCase()}...`}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                          }`}
                        />
                      )}

                      {fType === "currency" && (
                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">₹</span>
                          <input
                            type="text"
                            value={extraFieldValues[fieldKey] || ""}
                            onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                            placeholder="0"
                            className={`w-full border rounded-lg pl-8 pr-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                              hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                            }`}
                          />
                        </div>
                      )}

                      {fType === "date" && (
                        <input
                          type="date"
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                          }`}
                        />
                      )}

                      {fType === "time" && (
                        <input
                          type="time"
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                          }`}
                        />
                      )}

                      {fType === "dropdown" && (
                        <select
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all bg-white cursor-pointer ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200"
                          }`}
                        >
                          <option value="">Select {field.label}...</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}

                      {fType === "textarea" && (
                        <textarea
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          placeholder={`Enter ${field.label.toLowerCase()}...`}
                          rows={3}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                          }`}
                        />
                      )}

                      {fType === "toggle" && (
                        <div className="flex items-center gap-3 py-1">
                          <button
                            type="button"
                            onClick={() =>
                              updateFieldValue(
                                fieldKey,
                                extraFieldValues[fieldKey] === "true" ? "false" : "true"
                              )
                            }
                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                              extraFieldValues[fieldKey] === "true" ? "bg-indigo-600" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-xs transition-transform duration-200 ${
                                extraFieldValues[fieldKey] === "true" ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-600 font-medium">
                            {extraFieldValues[fieldKey] === "true" ? "Yes / Completed" : "No / Pending"}
                          </span>
                        </div>
                      )}

                      {fType === "text" && (
                        <input
                          type="text"
                          value={extraFieldValues[fieldKey] || ""}
                          onChange={(e) => updateFieldValue(fieldKey, e.target.value)}
                          placeholder={`Enter ${field.label.toLowerCase()}...`}
                          className={`w-full border rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all ${
                            hasError ? "border-red-400 bg-red-50/50" : "border-gray-200 bg-white"
                          }`}
                        />
                      )}

                      {hasError && (
                        <p className="text-xs text-red-500 mt-1 font-medium">{errors[fieldKey]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-sm font-semibold text-green-700">✓ No additional info needed for this stage</p>
              <p className="text-xs text-green-600 mt-1">You can confirm and move directly</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white whitespace-nowrap cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg whitespace-nowrap cursor-pointer transition-colors shadow-xs"
          >
            Save &amp; Move
          </button>
        </div>
      </div>
    </div>
  );
}